// src/components/Browser/TransactionModal.js
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatUnits } from 'ethers';
import { FaExclamationTriangle, FaFileContract, FaGasPump, FaInfoCircle, FaCheck } from 'react-icons/fa';
import { WalletContext } from '../../contexts/WalletContext';
import { NetworkContext } from '../../contexts/NetworkContext';
import { SettingsContext } from '../../contexts/SettingsContext';
import { safeParseUnits } from '../../utils/ethersHelpers';
import { getChainLogo } from '../../pages/Wallet/chainLogos';
import FaviconImage from './FaviconImage';
import './TransactionModal.css';

const TransactionModal = ({
  isOpen,
  onClose,
  transaction,
  onApprove,
  onReject,
  isLoading,
  site,
  isPermit, // Added isPermit prop to receive permit status from outside
  error
}) => {
  const { t } = useTranslation();
  const { currentWallet } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  const { gasOption, customGasPrice } = useContext(SettingsContext);
  
  const [expanded, setExpanded] = useState(false);
  const [gasSettings, setGasSettings] = useState({
    gasPrice: transaction?.gasPrice ? formatUnits(transaction.gasPrice, 'gwei') : customGasPrice,
    gasLimit: transaction?.gasLimit?.toString() || '21000'
  });
  
  // Parse transaction data for display
  const [txInfo, setTxInfo] = useState({
    to: '',
    value: '0',
    formattedValue: '0',
    data: '',
    isContractInteraction: false,
    methodName: '',
    decodedParams: [],
    estimatedFee: '0',
    isPermit: false, // Add flag for permit detection
  });

  // Decode transaction when it changes
  useEffect(() => {
    if (!transaction) return;
    
    const decodeTransaction = async () => {
      try {
        // Basic transaction info
        const to = transaction.to || '';
        const value = transaction.value ? transaction.value.toString() : '0';
        const formattedValue = formatUnits(value, 18); // Assuming ETH/native token
        const data = transaction.data || '0x';
        
        // Estimate gas fee
        const gasLimit = transaction.gasLimit?.toString() || gasSettings.gasLimit;
        const gasPrice = transaction.gasPrice 
          ? formatUnits(transaction.gasPrice, 'gwei') 
          : gasSettings.gasPrice;
        
        const estimatedFee = (
          parseFloat(gasLimit) * 
          parseFloat(gasPrice) * 
          1e-9
        ).toFixed(8);
        
        // Check if it's a contract interaction
        const isContractInteraction = data && data !== '0x';
        
        // Try to decode method signature (enhanced version with Permit detection)
        let methodName = '';
        let decodedParams = [];
        // Check if isPermit was passed from outside, use that first
        let localIsPermit = isPermit || false;
        
        if (isContractInteraction && data.length >= 10) {
          // Method signature is first 4 bytes (8 characters + '0x')
          const methodSignature = data.substring(0, 10);
          
          // If isPermit wasn't provided from outside, detect it here
          if (!localIsPermit) {
            // Enhanced check for Permit signatures
            if (methodSignature === '0xd505accf' || // Standard EIP-2612 permit
                methodSignature === '0x2612065' ||  // Another permit variant
                methodSignature === '0x8fcbaf0c' ||  // Permit2 batch method
                methodSignature === '0x04b0792f' ||  // Another permit variant
                methodSignature === '0xf3995c67' ||  // Another permit variant
                data.toLowerCase().includes('permit')) {
              localIsPermit = true;
              methodName = 'permit';
            }
          } else {
            // If it was already marked as permit, set the method name
            methodName = 'permit';
          }
          
          // If not a permit, try to identify other common methods
          if (!localIsPermit) {
            switch (methodSignature) {
              case '0xa9059cbb':
                methodName = 'transfer';
                break;
              case '0x095ea7b3':
                methodName = 'approve';
                break;
              case '0x23b872dd':
                methodName = 'transferFrom';
                break;
              // Add more common methods as needed
              default:
                methodName = methodSignature;
            }
          }
        }
        
        console.log(`Transaction parsed, isPermit=${localIsPermit}, methodName=${methodName}`);
        
        setTxInfo({
          to,
          value,
          formattedValue,
          data,
          isContractInteraction,
          methodName,
          isPermit: localIsPermit, // Store permit status
          decodedParams,
          estimatedFee
        });
        
      } catch (error) {
        console.error('Error decoding transaction:', error);
      }
    };
    
    decodeTransaction();
  }, [transaction, gasSettings, isPermit]); 
  
  // Update the gas settings when a transaction is permit
  useEffect(() => {
    if (txInfo.isPermit && transaction) {
      // Set higher gas settings for permit transactions
      setGasSettings(prev => ({
        ...prev,
        gasLimit: prev.gasLimit < "500000" ? "500000" : prev.gasLimit,
        gasPrice: parseFloat(prev.gasPrice) < 10 ? "10" : prev.gasPrice // Ensure minimum gas price
      }));
    }
  }, [txInfo.isPermit, transaction]);
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onReject();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onReject]);
  
  const handleGasChange = (e) => {
    const { name, value } = e.target;
    setGasSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleApprove = () => {
    try {
      // Apply gas settings to transaction
      const updatedTx = {
        ...transaction,
        // Use safeParseUnits for gasPrice to handle potential errors
        gasPrice: safeParseUnits(gasSettings.gasPrice, 'gwei'),
        gasLimit: gasSettings.gasLimit
      };
      
      // If this is a permit transaction, ensure the gas limit is high enough
      if (txInfo.isPermit) {
        console.log('Setting higher gas limit for permit transaction');
        updatedTx.gasLimit = gasSettings.gasLimit > 300000 ? gasSettings.gasLimit : '500000';
      }
      
      onApprove(updatedTx);
    } catch (error) {
      console.error('Error preparing transaction:', error);
      alert(t('browser.transactionRequest.errorPreparing', 'Error preparing transaction. Please try again.'));
    }
  };
  
  // Handle modal click to prevent overlay click propagation
  const handleModalClick = (e) => {
    e.stopPropagation();
  };
  
  if (!isOpen || !transaction) return null;
  
  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Extract hostname
  const hostname = site ? new URL(site).hostname : '';
  
  return (
    <div className="transaction-modal-overlay" onClick={onReject}>
      <div className="transaction-modal" onClick={handleModalClick}>
        <h2>{t('browser.transactionRequest.title', 'Transaction Request')}</h2>
        
        <div className="site-info">
          <div className="site-favicon">
            <FaviconImage hostname={hostname} className="site-favicon-img" alt={hostname} />
          </div>
          <div className="site-details">
            <h3>{hostname}</h3>
            <p className="site-url">{site}</p>
          </div>
        </div>
        
        <div className="transaction-warning">
          <FaExclamationTriangle className="warning-icon" />
          <p>{t('browser.transactionRequest.warning', 'Review this transaction carefully before approving')}</p>
        </div>
        
        {/* Special info section for permit transactions */}
        {txInfo.isPermit && (
          <div className="transaction-info permit-info">
            <FaInfoCircle className="info-icon" />
            <p>{t('browser.transactionRequest.permitDescription', 'This is a token approval that will allow the application to use your tokens.')}</p>
          </div>
        )}
        
        {/* Display error message if any */}
        {error && (
          <div className="transaction-error">
            <FaExclamationTriangle className="error-icon" />
            <p>{error}</p>
          </div>
        )}
        
        <div className="transaction-details">
          <h4>{t('browser.transactionRequest.details', 'Transaction Details')}</h4>
          
          <div className="transaction-detail-item">
            <span className="detail-label">{t('browser.transactionRequest.from', 'From')}</span>
            <span className="detail-value">{formatAddress(currentWallet?.address)}</span>
          </div>
          
          <div className="transaction-detail-item">
            <span className="detail-label">{t('browser.transactionRequest.to', 'To')}</span>
            <span className="detail-value">{formatAddress(txInfo.to)}</span>
          </div>
          
          {parseFloat(txInfo.formattedValue) > 0 && (
            <div className="transaction-detail-item">
              <span className="detail-label">{t('browser.transactionRequest.value', 'Value')}</span>
              <span className="detail-value">
                {txInfo.formattedValue} {currentChain?.nativeTokenSymbol || 'ETH'}
              </span>
            </div>
          )}
          
          {txInfo.isContractInteraction && (
            <div className="transaction-detail-item">
              <span className="detail-label">{t('browser.transactionRequest.action', 'Action')}</span>
              <span className="detail-value">{txInfo.methodName || t('browser.transactionRequest.contractInteraction', 'Contract Interaction')}</span>
            </div>
          )}
          
          {/* Special highlight for Permit transactions */}
          {txInfo.isPermit && (
            <div className="transaction-detail-item permit-highlight">
              <span className="detail-label">{t('browser.transactionRequest.permitInfo', 'Permit Action')}</span>
              <span className="detail-value permit-value">
                <FaCheck className="permit-icon" />
                {t('browser.transactionRequest.tokenApproval', 'Token Approval')}
              </span>
            </div>
          )}
          
          <div className="transaction-detail-item">
            <span className="detail-label">{t('browser.transactionRequest.estimatedFee', 'Est. Fee')}</span>
            <span className="detail-value">
              {txInfo.estimatedFee} {currentChain?.nativeTokenSymbol || 'ETH'}
            </span>
          </div>
        </div>
        
        <div className="gas-settings">
          <div className="gas-settings-header" onClick={() => setExpanded(!expanded)}>
            <FaGasPump className="gas-icon" />
            <h4>{t('browser.transactionRequest.gasSettings', 'Gas Settings')}</h4>
            <span className="expand-icon">{expanded ? '▲' : '▼'}</span>
          </div>
          
          {expanded && (
            <div className="gas-settings-content">
              <div className="gas-input-group">
                <label>{t('browser.transactionRequest.gasPrice', 'Gas Price')} (Gwei)</label>
                <input 
                  type="number" 
                  name="gasPrice"
                  value={gasSettings.gasPrice}
                  onChange={handleGasChange}
                  min={txInfo.isPermit ? "5" : "0"}
                  step="0.1"
                />
              </div>
              
              <div className="gas-input-group">
                <label>{t('browser.transactionRequest.gasLimit', 'Gas Limit')}</label>
                <input 
                  type="number" 
                  name="gasLimit"
                  value={gasSettings.gasLimit}
                  onChange={handleGasChange}
                  min={txInfo.isPermit ? "300000" : "21000"} // Higher minimum for permits
                  step="1000"
                />
              </div>
              
              {txInfo.isPermit && (
                <div className="gas-warning">
                  <FaExclamationTriangle className="warning-icon-small" />
                  <p>{t('browser.transactionRequest.permitGasWarning', 'Permit transactions may require higher gas limits.')}</p>
                </div>
              )}
              
              <div className="gas-info">
                <FaInfoCircle className="info-icon" />
                <p>{t('browser.transactionRequest.gasInfo', 'Higher gas price = faster transaction but more expensive')}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="network-info">
          <h4>{t('browser.transactionRequest.network', 'Network')}</h4>
          <div className="network-details">
            <div className="network-icon">
              <img 
                src={getChainLogo(currentChain?.key)} 
                alt={currentChain?.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/wallet-logo.png';
                }}
              />
            </div>
            <span>{currentChain?.name}</span>
          </div>
        </div>
        
        {txInfo.data !== '0x' && (
          <div className="transaction-data">
            <h4>{t('browser.transactionRequest.data', 'Transaction Data')}</h4>
            <div className="data-content">
              <code>{txInfo.data}</code>
            </div>
          </div>
        )}
        
        <div className="transaction-actions">
          <button 
            className="reject-button"
            onClick={onReject}
            disabled={isLoading}
          >
            {t('browser.transactionRequest.reject', 'Reject')}
          </button>
          <button 
            className="approve-button"
            onClick={handleApprove}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-spinner"></span>
            ) : null}
            {t('browser.transactionRequest.confirm', 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;